import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable, unwrap } from 'jotai/utils';
import { IMetricFundDataModel, IMetricsResponseDataModel } from '../../data-models/metrics.data-model';
import { queryClient } from '../MaggieQueryClient';
import {
  companyMetricsQueryKeys,
  companyMetricsQueryOptions,
  CompanyMetricsQueryParams,
} from './CompanyMetricsByIdState';

type Params = CompanyMetricsQueryParams;
type MetricsValue = IMetricsResponseDataModel | null | Promise<IMetricsResponseDataModel | null>;

const paramsEqual = (a: Params, b: Params) =>
  a.companyId === b.companyId && a.asOfDate === b.asOfDate && a.currency === b.currency;

/**
 * Reads route through React Query so the Jotai atom and `useCompanyMetricsById`
 * always share the same cache entry for matching `{ companyId, asOfDate?, currency? }`.
 *
 * Writes should go through {@link setCompanyMetricsAtomJ} which updates both the
 * Jotai atom and the React Query cache in one call. Direct
 * `set(companyMetricsAtomJ(params), value)` only updates Jotai and is discouraged
 * for production code.
 */
export const companyMetricsAtomJ = atomFamily(
  (params: Params) =>
    atomWithDefault<MetricsValue>(() => {
      // Guard against the `-1` sentinel used by callers when no company is
      // selected (e.g. `restructureIdToPositionMapAtomJ(companyId ?? -1)` in
      // `securityUtils.ts`). Aligns with the `companyId >= 0` convention in
      // `CompanyStateJ`.
      if (!params.companyId || params.companyId < 0) {
        return null;
      }
      return queryClient.ensureQueryData(companyMetricsQueryOptions(params));
    }),
  paramsEqual
);

export const companyMetricsLoadableJ = atomFamily(
  (params: Params) => loadable(companyMetricsAtomJ(params)),
  paramsEqual
);

/**
 * Write-only atom: keeps the Jotai atom and the React Query cache in lockstep.
 *
 * Accepts either a literal value or a functional updater `(prev) => next`.
 * The updater receives the current value from React Query (sync peek — no fetch).
 */
export const setCompanyMetricsAtomJ = atom(
  null,
  (
    _get,
    set,
    payload: {
      params: Params;
      value:
        | IMetricsResponseDataModel
        | null
        | ((prev: IMetricsResponseDataModel | null | undefined) => IMetricsResponseDataModel | null);
    }
  ) => {
    const prev = queryClient.getQueryData<IMetricsResponseDataModel | null>(
      companyMetricsQueryKeys.byId(payload.params)
    );
    const next =
      typeof payload.value === 'function'
        ? (
            payload.value as (
              p: IMetricsResponseDataModel | null | undefined
            ) => IMetricsResponseDataModel | null
          )(prev)
        : payload.value;

    set(companyMetricsAtomJ(payload.params), next);
    queryClient.setQueryData(companyMetricsQueryKeys.byId(payload.params), next);
  }
);

/** Synchronous peek of the current React Query cache entry. Never triggers a fetch. */
export function peekCompanyMetrics(params: Params): IMetricsResponseDataModel | null | undefined {
  return queryClient.getQueryData<IMetricsResponseDataModel | null>(companyMetricsQueryKeys.byId(params));
}

/**
 * Derived: map of restructureId → position read off the latest metrics
 * payload for a company.
 *
 * Uses `unwrap` so consumers stay synchronous: returns an empty Map while the
 * underlying metrics are loading.
 */
export const restructureIdToPositionMapAtomJ = atomFamily((companyId: number) =>
  atom((get) => {
    const value = get(unwrap(companyMetricsAtomJ({ companyId })));
    const result = new Map<string, string>();
    value?.metrics?.at(0)?.transactions?.forEach((transaction) => {
      if (transaction.restructureId && transaction.position) {
        result.set(transaction.restructureId, transaction.position);
      }
    });
    return result;
  })
);

/**
 * Derived: map of fundId → IMetricFundDataModel. Returns an empty Map while
 * metrics are loading.
 */
export const metricsFundDataByFundIdAtomJ = atomFamily((companyId: number) =>
  atom((get) => {
    const fundData = get(unwrap(companyMetricsAtomJ({ companyId })))?.metrics?.at(0)?.fundData ?? [];
    const result = new Map<number, IMetricFundDataModel>();
    for (const fd of fundData) {
      result.set(fd.fundId, fd);
    }
    return result;
  })
);
